import { useState } from "react";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "@/components/cobrain/tokens";

/* Named verticals + the workflow we'd automate first for each.
   Chip filter + detail panel — deliberately a different rhythm to the
   card grids above and below it. */

const VERTICALS = [
  {
    name: "Trades & home services",
    first: "Missed-call callback and same-day booking",
    detail: "Every unanswered call gets a callback within a minute, qualifies the job, and drops it straight into the calendar. The owner stops losing work to whoever picked up first.",
    also: ["Quote follow-up until they answer", "Job reminders and reschedules", "Review request after completion"],
  },
  {
    name: "Clinics & practices",
    first: "Recall, reminders and no-show recovery",
    detail: "Patients due for a recall get contacted on schedule, confirmations go out automatically, and a no-show triggers a rebooking attempt the same day.",
    also: ["New patient intake", "Waitlist backfill on cancellations", "Post-visit follow-up"],
  },
  {
    name: "Real estate",
    first: "Lead triage and instant enquiry response",
    detail: "Portal and web enquiries get answered in under a minute, qualified against the listing, and routed to the right agent with the context already gathered.",
    also: ["Viewing scheduling", "Vendor update cadence", "Past-buyer reactivation"],
  },
  {
    name: "Agencies",
    first: "Proposal follow-up until someone replies",
    detail: "Every proposal gets a follow-up sequence that stops the moment a human replies, so nothing sits unread and nobody has to remember to chase it.",
    also: ["Inbound brief qualification", "Client status updates", "Renewal reminders"],
  },
  {
    name: "Professional services",
    first: "Intake, scheduling and document chase-ups",
    detail: "New matters get intake questions answered, a slot booked, and outstanding documents chased on a cadence — without a paralegal doing it by hand.",
    also: ["Conflict-check prep", "Appointment confirmations", "Invoice follow-up"],
  },
  {
    name: "E-commerce ops",
    first: "Support triage and order-status answers",
    detail: "Order-status and returns questions get resolved from your own systems, and only the genuinely unusual tickets reach a person.",
    also: ["Refund policy handling", "Abandoned-cart follow-up", "Supplier chase-ups"],
  },
];

export function VerticalStrip() {
  const [active, setActive] = useState(0);
  const v = VERTICALS[active];

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div role="tablist" aria-label="Industries" className="svc-vert-chips" style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
        {VERTICALS.map((item, i) => {
          const on = i === active;
          return (
            <button
              key={item.name}
              role="tab"
              aria-selected={on}
              type="button"
              onClick={() => setActive(i)}
              style={{
                cursor: "pointer", minHeight: 40, padding: "9px 16px", borderRadius: 999,
                fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600,
                color: on ? "#fff" : PALETTE.ink2,
                background: on ? PALETTE.ink : PALETTE.card,
                border: `1px solid ${on ? PALETTE.ink : PALETTE.line}`,
                transition: "background 180ms ease, color 180ms ease",
                whiteSpace: "nowrap",
              }}
            >
              {item.name}
            </button>
          );
        })}
      </div>

      <div
        key={v.name}
        className="svc-vert-panel"
        style={{
          display: "grid", gridTemplateColumns: "minmax(0,1.15fr) minmax(0,0.85fr)", gap: "clamp(20px, 4vw, 44px)",
          background: PALETTE.card, border: `1px solid ${PALETTE.line}`, borderRadius: 20,
          padding: "clamp(20px, 3vw, 30px)",
        }}
      >
        <div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, fontWeight: 700, marginBottom: 10 }}>
            First thing we'd automate
          </div>
          <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 400, fontSize: "clamp(24px, 3.2vw, 32px)", margin: "0 0 12px", letterSpacing: "-0.01em", lineHeight: 1.15 }}>
            {v.first}
          </h3>
          <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.6, color: PALETTE.ink2 }}>{v.detail}</p>
        </div>
        <div style={{ borderLeft: `1px solid ${PALETTE.line}`, paddingLeft: "clamp(0px, 2vw, 24px)" }} className="svc-vert-aside">
          <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700, marginBottom: 12 }}>
            Usually next
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {v.also.map((a) => (
              <li key={a} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.5, color: PALETTE.ink2 }}>
                <span aria-hidden style={{ color: PALETTE.amber, fontWeight: 700 }}>·</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style>{`
        .svc-vert-panel { animation: svcPanelIn 320ms ease-out both; }
        @keyframes svcPanelIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .svc-vert-panel { animation: none; } }
        @media (max-width: 820px) {
          .svc-vert-panel { grid-template-columns: 1fr !important; }
          .svc-vert-aside { border-left: 0 !important; padding-left: 0 !important; border-top: 1px solid rgba(0,0,0,0.08); padding-top: 18px !important; }
        }
        @media (max-width: 620px) {
          .svc-vert-chips { flex-wrap: nowrap; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
          .svc-vert-chips::-webkit-scrollbar { display: none; }
          .svc-vert-chips > button { scroll-snap-align: start; flex: 0 0 auto; }
        }
      `}</style>
    </div>
  );
}

export default VerticalStrip;
